import Phaser from 'phaser';
import { TILE, PLAYER_BASE_SPEED } from '../../types';
import { scaleSpriteToTile, fitBodyToTile } from '../../systems/SpriteScaling';

export type Direction = 'up' | 'down' | 'left' | 'right';
export const ALL_DIRS: Direction[] = ['up', 'down', 'left', 'right'];

export function reverseOf(d: Direction): Direction {
  switch (d) {
    case 'up': return 'down';
    case 'down': return 'up';
    case 'left': return 'right';
    case 'right': return 'left';
  }
}

export interface GridQuery {
  isBlocked(col: number, row: number): boolean;
}

export abstract class BaseEnemy extends Phaser.Physics.Arcade.Sprite {
  readonly speedFactor: number;
  alive = true;
  protected grid: GridQuery;
  protected currentDir: Direction = 'down';
  protected baseScale = 1;
  protected animPhase = 0; // accumulator for walk animation

  constructor(
    scene: Phaser.Scene,
    texKey: string,
    col: number,
    row: number,
    speedFactor: number,
    grid: GridQuery,
  ) {
    const x = col * TILE + TILE / 2;
    const y = row * TILE + TILE / 2;
    super(scene, x, y, texKey);
    // Cap at 0.84 to prevent physics tunnelling at high speed (TILE=48px, ~176px/s max)
    this.speedFactor = Math.min(speedFactor, 0.84);
    this.grid = grid;
    scene.add.existing(this);
    this.baseScale = scaleSpriteToTile(this, TILE - 8);
    scene.physics.add.existing(this);
    fitBodyToTile(this, TILE - 16);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);
  }

  /**
   * Dynablaster-style walk animation: while the body has velocity, waddle
   * left/right with a small angle, bounce slightly vertically, and pulse
   * the scale to imitate alternating-foot frames. Faces left/right via flipX.
   */
  protected animateWalk(delta: number) {
    if (!this.alive) return;
    const body = this.body as Phaser.Physics.Arcade.Body;
    const moving = body.velocity.x !== 0 || body.velocity.y !== 0;
    // Sprite art faces RIGHT by default; flip when moving left.
    if (body.velocity.x < -1) this.setFlipX(true);
    else if (body.velocity.x > 1) this.setFlipX(false);

    if (moving) {
      this.animPhase += delta * 0.012;
      const sway = Math.sin(this.animPhase) * 8;       // ±8° waddle
      const pulse = 1 + Math.sin(this.animPhase) * 0.07; // 7% scale beat
      this.setAngle(sway);
      this.setScale(this.baseScale * pulse);
    } else {
      // Snap back to neutral
      this.setAngle(0);
      this.setScale(this.baseScale);
    }
  }

  protected get speed(): number {
    return PLAYER_BASE_SPEED * this.speedFactor;
  }

  gridCol(): number { return Math.floor(this.x / TILE); }
  gridRow(): number { return Math.floor(this.y / TILE); }

  protected centerOfTile(): { x: number; y: number; col: number; row: number } {
    const col = this.gridCol();
    const row = this.gridRow();
    return { x: col * TILE + TILE / 2, y: row * TILE + TILE / 2, col, row };
  }

  protected isCenteredOnTile(tol = 3): boolean {
    const { x, y } = this.centerOfTile();
    return Math.abs(this.x - x) < tol && Math.abs(this.y - y) < tol;
  }

  protected canMoveTo(dir: Direction): boolean {
    const { col, row } = this.centerOfTile();
    const [dc, dr] = this.dirToDelta(dir);
    return !this.grid.isBlocked(col + dc, row + dr);
  }

  protected dirToDelta(dir: Direction): [number, number] {
    switch (dir) {
      case 'up': return [0, -1];
      case 'down': return [0, 1];
      case 'left': return [-1, 0];
      case 'right': return [1, 0];
    }
  }

  protected applyDir(dir: Direction) {
    const [dc, dr] = this.dirToDelta(dir);
    (this.body as Phaser.Physics.Arcade.Body).setVelocity(dc * this.speed, dr * this.speed);
    this.currentDir = dir;
  }

  protected haltMovement() {
    (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
  }

  kill() {
    if (!this.alive) return;
    this.alive = false;
    this.haltMovement();
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scale: 0.2,
      duration: 250,
      onComplete: () => this.destroy(),
    });
  }

  abstract update(time: number, delta: number): void;
}
